import api from '../../../api/client';

// Types
export interface AIProvider {
  id: string;
  userId: string;
  name: string;
  providerType: 'openai' | 'anthropic' | 'custom';
  baseUrl: string | null;
  defaultModel: string;
  isDefault: boolean;
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProviderInput {
  name: string;
  providerType: 'openai' | 'anthropic' | 'custom';
  apiKey: string;
  baseUrl?: string;
  defaultModel: string;
  isDefault?: boolean;
  settings?: {
    maxTokens?: number;
    temperature?: number;
  };
}

export interface UpdateProviderInput {
  name?: string;
  apiKey?: string;
  baseUrl?: string | null;
  defaultModel?: string;
  isDefault?: boolean;
  settings?: {
    maxTokens?: number;
    temperature?: number;
  };
}

// Actions configuration for when an email matches an AI column
export interface ActionsConfig {
  onMatch: {
    moveToFolder?: string | null;
    setCategory?: string | null;
    createTask?: {
      listId: string;
      titleMode: 'subject' | 'custom' | 'ai';
      customTitle?: string;
      dueDays?: number;
      includeMailLink: boolean;
    } | null;
  };
  executeOnce: boolean;
  requireMinConfidence?: number | null;
}

export interface SmartAIColumn {
  id: string;
  boardId: string;
  name: string;
  baseSourceType: 'folder' | 'category' | 'label' | 'all';
  baseSourceId: string;
  prompt: string;
  systemPrompt: string | null;
  providerId: string | null;
  position: number;
  color: string | null;
  icon: string | null;
  enabled: boolean;
  confidenceThreshold: number; // 0-100, default 80
  actionsConfig: ActionsConfig | null;
  reEvalConfig: {
    onThreadUpdate: boolean;
    onDeadlineApproach: boolean;
    periodicDays: number | null;
    maxReEvals: number;
  };
  costConfig: {
    maxTokensPerDay: number;
    maxEvalsPerHour: number;
    skipConfidenceAbove: number;
    cacheDays: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AIColumnTemplate {
  id: string;
  name: string;
  icon: string;
  color: string;
  confidenceThreshold: number;
  prompt: string;
  description: string;
}

export interface CreateSmartColumnInput {
  boardId: string;
  name: string;
  baseSourceType?: 'folder' | 'category' | 'label' | 'all';
  baseSourceId?: string;
  prompt: string;
  systemPrompt?: string;
  providerId?: string;
  position?: number;
  color?: string;
  icon?: string;
  confidenceThreshold?: number;
  actionsConfig?: ActionsConfig | null;
  reEvalConfig?: Partial<SmartAIColumn['reEvalConfig']>;
  costConfig?: Partial<SmartAIColumn['costConfig']>;
}

export interface UpdateSmartColumnInput {
  name?: string;
  baseSourceType?: 'folder' | 'category' | 'label' | 'all';
  baseSourceId?: string;
  prompt?: string;
  systemPrompt?: string | null;
  providerId?: string | null;
  position?: number;
  color?: string | null;
  icon?: string | null;
  enabled?: boolean;
  confidenceThreshold?: number;
  actionsConfig?: ActionsConfig | null;
  reEvalConfig?: Partial<SmartAIColumn['reEvalConfig']>;
  costConfig?: Partial<SmartAIColumn['costConfig']>;
}

export interface AIEvaluation {
  id: string;
  columnId: string;
  mailAccountId: string;
  messageId: string;
  matches: boolean;
  confidence: number;
  reasoning: string | null;
  reEvalAt: string | null;
  reEvalReason: string | null;
  evalCount: number;
  tokensUsed: number | null;
  latencyMs: number | null;
  modelUsed: string | null;
  evaluatedAt: string;
}

export interface ColumnStats {
  totalEvaluations: number;
  matchCount: number;
  avgConfidence: number;
  tokensUsedToday: number;
  evalsLastHour: number;
}

// AI Providers API - using central ky client with auto-auth
export const aiProvidersApi = {
  list: async (): Promise<AIProvider[]> => {
    const data = await api.get('ai-providers').json<{ providers: AIProvider[] }>();
    return data.providers;
  },

  get: async (providerId: string): Promise<AIProvider> => {
    const data = await api.get(`ai-providers/${providerId}`).json<{ provider: AIProvider }>();
    return data.provider;
  },

  create: async (input: CreateProviderInput): Promise<AIProvider> => {
    const data = await api.post('ai-providers', { json: input }).json<{ provider: AIProvider }>();
    return data.provider;
  },

  update: async (providerId: string, input: UpdateProviderInput): Promise<AIProvider> => {
    const data = await api.patch(`ai-providers/${providerId}`, { json: input }).json<{ provider: AIProvider }>();
    return data.provider;
  },

  delete: async (providerId: string): Promise<void> => {
    await api.delete(`ai-providers/${providerId}`);
  },

  test: async (providerId: string): Promise<{ success: boolean; error?: string }> => {
    const data = await api.post(`ai-providers/${providerId}/test`).json<{ success: boolean; error?: string }>();
    return data;
  },

  listModels: async (providerId: string): Promise<string[]> => {
    const data = await api.get(`ai-providers/${providerId}/models`).json<{ models: string[] }>();
    return data.models;
  },
};

// Smart AI Columns API
export const smartColumnsApi = {
  list: async (boardId: string): Promise<SmartAIColumn[]> => {
    const data = await api.get(`boards/${boardId}/ai-columns`).json<{ columns: SmartAIColumn[] }>();
    return data.columns;
  },

  getTemplates: async (): Promise<AIColumnTemplate[]> => {
    const data = await api.get('ai-columns/templates').json<{ templates: AIColumnTemplate[] }>();
    return data.templates;
  },

  createFromTemplate: async (templateId: string, boardId: string, providerId?: string): Promise<SmartAIColumn> => {
    const data = await api.post('ai-columns/from-template', {
      json: { templateId, boardId, providerId },
    }).json<{ column: SmartAIColumn }>();
    return data.column;
  },

  get: async (columnId: string): Promise<SmartAIColumn> => {
    const data = await api.get(`ai-columns/${columnId}`).json<{ column: SmartAIColumn }>();
    return data.column;
  },

  create: async (input: CreateSmartColumnInput): Promise<SmartAIColumn> => {
    const data = await api.post('ai-columns', { json: input }).json<{ column: SmartAIColumn }>();
    return data.column;
  },

  update: async (columnId: string, input: UpdateSmartColumnInput): Promise<SmartAIColumn> => {
    const data = await api.patch(`ai-columns/${columnId}`, { json: input }).json<{ column: SmartAIColumn }>();
    return data.column;
  },

  delete: async (columnId: string): Promise<void> => {
    await api.delete(`ai-columns/${columnId}`);
  },

  getEvaluations: async (columnId: string, matchesOnly = false): Promise<AIEvaluation[]> => {
    const data = await api.get(`ai-columns/${columnId}/evaluations`, {
      searchParams: { matchesOnly: String(matchesOnly) },
    }).json<{ evaluations: AIEvaluation[] }>();
    return data.evaluations;
  },

  getStats: async (columnId: string): Promise<ColumnStats> => {
    const data = await api.get(`ai-columns/${columnId}/stats`).json<{ stats: ColumnStats }>();
    return data.stats;
  },

  triggerEvaluation: async (columnId: string): Promise<void> => {
    await api.post(`ai-columns/${columnId}/evaluate`);
  },
  
  runActionsOnce: async (columnId: string, mailAccountId: string): Promise<{ processed: number; executed: string[]; errors: string[] }> => {
    const data = await api.post(`ai-columns/${columnId}/run-actions`, { 
      json: { mailAccountId } 
    }).json<{ processed: number; executed: string[]; errors: string[] }>();
    return data;
  },
};
